"use client";

import { ArrowRight, Bandage, Brush, Drill, Shield, Siren, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurCoreServices() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const services = [{
    spec: "[MOW.01 / PRECISION]",
    title: "Mowing",
    description: "Architectural blade height management. We maintain the perfect strata for your specific turf variety.",
    Icon: Brush
  }, {
    spec: "[EDG.02 / DEFINITION]",
    title: "Edging",
    description: "Hard-line definition along walkways and driveways. Zero-tolerance for overgrowth on concrete.",
    Icon: Shield
  }, {
    spec: "[TRM.03 / SCULPTING]",
    title: "Trimming",
    description: "Detailed perimeter control around obstacles. We reach the angles where the heavy machinery cannot.",
    Icon: Drill
  }, {
    spec: "[CLN.04 / RECOVERY]",
    title: "Seasonal Cleanup",
    description: "Debris extraction and leaf management. Restoring the field to its baseline state after every season.",
    Icon: Bandage
  }, {
    spec: "[FER.05 / NUTRIENTS]",
    title: "Fertilization",
    description: "Chemical engineering for soil health. Targeted nutrient delivery for deep emerald pigmentation.",
    Icon: Siren
  }, {
    spec: "[AER.06 / AIRFLOW]",
    title: "Aeration",
    description: "Core extraction to facilitate root-level oxygen exchange. Essential for long-term turf structural integrity.",
    Icon: Sparkles
  }];
  return <section data-section-id="1791" id="our-core-services" className="relative bg-background text-foreground py-28 md:py-40 overflow-hidden">
      {}
      <div aria-hidden="true" className="absolute top-0 left-0 right-0 h-6 bg-primary" style={{
      backgroundImage: "repeating-linear-gradient(45deg, hsl(150 20% 6%) 0, hsl(150 20% 6%) 4px, hsl(142 76% 45%) 4px, hsl(142 76% 45%) 8px)"
    }} />

      <div className="max-w-[1360px] mx-auto px-6 md:px-8 lg:px-16">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 mb-16 md:mb-24 transition-all duration-500 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-8">
            {}
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-2.5 h-2.5 bg-primary" />
              <span className="font-mono text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-muted-foreground">
                § 02 — The Service Catalog
              </span>
            </div>

            <h2 className="font-default font-extrabold uppercase text-foreground leading-[0.9] tracking-tighter text-5xl md:text-6xl lg:text-7xl xl:text-[5rem]">
              The <em className="not-italic italic text-primary-foreground bg-primary px-2">Precision</em> of 
              Acosta's Blade.
            </h2>
          </div>

          <div className="lg:col-span-4 flex flex-col justify-end">
            <p className="font-mono text-sm leading-relaxed uppercase tracking-wider text-muted-foreground mb-6">
              [SPEC-02 / CATALOG] — Engineering the perfect lawn through technical mowing, edging, and trimming. 
              No shortcuts, just high-precision maintenance for your landscape.
            </p>
            <Link to="/GetQuotePage" className="group inline-flex items-center gap-2 font-mono text-sm font-semibold uppercase tracking-widest text-foreground hover:text-primary transition-colors duration-200">
              <span>Request a service quote</span>
              <ArrowRight className="w-4 h-4 text-primary transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {}
        <div aria-hidden="true" className="h-1.5 w-32 mb-0" style={{
        backgroundImage: "repeating-linear-gradient(45deg, hsl(142 76% 45%) 0, hsl(142 76% 45%) 12px, hsl(150 20% 6%) 12px, hsl(150 20% 6%) 24px)"
      }} />

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t-2 border-l-2 border-foreground">
          {services.map((service, i) => {
          const Icon = service.Icon;
          return <article key={i} data-index={i} className="group relative bg-card border-r-2 border-b-2 border-foreground p-8 min-h-[280px] flex flex-col transition-colors duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:bg-primary cursor-pointer">
                {}
                <div className="flex items-start justify-between mb-auto">
                  <Icon className="w-9 h-9 stroke-[1.5] text-muted-foreground transition-colors duration-200 group-hover:text-primary-foreground" strokeWidth={1.5} />
                  <span className="font-mono text-xs font-medium tracking-[0.12em] uppercase text-muted-foreground transition-colors duration-300 group-hover:text-primary-foreground">
                    {service.spec}
                  </span>
                </div>

                {}
                <div className="relative inline-block mt-8 mb-3">
                  <h3 className="font-default font-bold uppercase text-2xl md:text-[1.75rem] leading-none tracking-tight text-foreground transition-colors duration-300 group-hover:text-primary-foreground">
                    {service.title}
                  </h3>
                  <span aria-hidden="true" className="absolute left-0 -bottom-2 h-[3px] w-0 bg-foreground transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:w-full" />
                </div>

                {}
                <p className="font-default text-base leading-relaxed text-muted-foreground transition-colors duration-300 group-hover:text-primary-foreground/90 mt-4">
                  {service.description}
                </p>

                {}
                <div className="mt-6 flex justify-end">
                  <span className="inline-flex items-center gap-1.5 font-mono text-xs font-medium uppercase tracking-wider text-foreground transition-colors duration-300 group-hover:text-primary-foreground">
                    <ArrowRight className="w-3.5 h-3.5" />
                    View Specs
                  </span>
                </div>
              </article>;
        })}
        </div>
      </div>
    </section>;
}